import React from 'react';
import { Users, FileText, MessageSquare, AlertCircle, TrendingUp } from 'lucide-react';

const AnalyticsDashboard = ({ analytics, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF8C00] border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load analytics data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#7B3F9E]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.total_users}</p>
            </div>
            <Users className="w-10 h-10 text-[#7B3F9E] opacity-50" />
          </div>
        </div>

        {/* Total Posts */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#FF8C00]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Posts</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.total_posts}</p>
            </div>
            <FileText className="w-10 h-10 text-[#FF8C00] opacity-50" />
          </div>
        </div>

        {/* Total Comments */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Comments</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.total_comments}</p>
            </div>
            <MessageSquare className="w-10 h-10 text-blue-500 opacity-50" />
          </div>
        </div>

        {/* Pending Verification */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.pending_verification}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-yellow-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Active Users */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <TrendingUp className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Active Users (7 days)</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">{analytics.active_users_7d}</p>
          <p className="text-sm text-gray-500 mt-1">Users who posted or commented recently</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Active Users (30 days)</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">{analytics.active_users_30d}</p>
          <p className="text-sm text-gray-500 mt-1">Monthly active contributors</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">Flagged Content</h3>
          </div>
          <p className="text-3xl font-bold text-red-600">{analytics.flagged_content_count}</p>
          <p className="text-sm text-gray-500 mt-1">Pending review</p>
        </div>
      </div>

      {/* Top Contributors */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Top Contributors</h3>
        {analytics.top_contributors.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No contributors yet</p>
        ) : (
          <div className="space-y-3">
            {analytics.top_contributors.map((contributor, index) => (
              <div
                key={contributor.user_id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-gray-400' :
                    index === 2 ? 'bg-orange-600' :
                    'bg-[#7B3F9E]'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{contributor.screenname}</p>
                    <p className="text-sm text-gray-500">
                      {contributor.total_posts} posts · {contributor.total_comments} comments
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#FF8C00]">{contributor.total_contributions}</p>
                  <p className="text-xs text-gray-500">total</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
